import React, { useEffect, useMemo, useState } from "react";
import {
  Column,
  DataTable,
  Grid,
  Heading,
  InlineLoading,
  InlineNotification,
  Link,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from "@carbon/react";
import { useIntl } from "react-intl";
import { getFromOpenElisServer } from "../utils/Utils";
import DeliveryIssuesPanel from "./DeliveryIssuesPanel";

const EVENT_TYPE_MESSAGES = {
  AST_RESULT_AVAILABLE: "analyzer.importIssues.event.astResultAvailable",
  AST_QC_FAIL: "analyzer.importIssues.event.astQcFail",
};

const FAILURE_REASON_MESSAGES = {
  AST_ANALYZER_RUN_NOT_MATCHED:
    "analyzer.importIssues.failure.astRunNotMatched",
  AST_ANALYZER_EVENT_PROCESSING_FAILED:
    "analyzer.importIssues.failure.processingFailed",
};

const formatCode = (intl, value, messageIds, fallbackId) => {
  if (!value) {
    return "-";
  }
  const messageId = messageIds[value];
  if (messageId) {
    return intl.formatMessage({ id: messageId });
  }
  return intl.formatMessage({ id: fallbackId }, { code: value });
};

const formatEventType = (intl, value) =>
  formatCode(
    intl,
    value,
    EVENT_TYPE_MESSAGES,
    "analyzer.importIssues.event.unknown",
  );

const formatFailureReason = (intl, value) =>
  formatCode(
    intl,
    value,
    FAILURE_REASON_MESSAGES,
    "analyzer.importIssues.failure.unknown",
  );

const ImportIssuesTable = ({ headers, rows, title, description }) => (
  <DataTable rows={rows} headers={headers}>
    {({
      rows: tableRows,
      headers: tableHeaders,
      getHeaderProps,
      getRowProps,
    }) => (
      <TableContainer title={title} description={description}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableHeader key={header.key} {...getHeaderProps({ header })}>
                  {header.header}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => (
              <TableRow key={row.id} {...getRowProps({ row })}>
                {row.cells.map((cell) => (
                  <TableCell key={cell.id}>{cell.value}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </DataTable>
);

const ImportIssuesPanel = () => {
  const intl = useIntl();
  const [issues, setIssues] = useState({ eventRows: [] });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    getFromOpenElisServer("/rest/analyzer/import-issues", (response) => {
      const data = response?.status === "success" ? response.data : null;
      setIssues({
        eventRows: data?.eventRows || [],
      });
      setLoadFailed(!data);
      setLoading(false);
    });
  }, []);

  const eventHeaders = useMemo(
    () => [
      {
        key: "eventType",
        header: intl.formatMessage({ id: "analyzer.importIssues.event" }),
      },
      {
        key: "sourceId",
        header: intl.formatMessage({ id: "analyzer.importIssues.source" }),
      },
      {
        key: "targetReference",
        header: intl.formatMessage({ id: "analyzer.importIssues.target" }),
      },
      {
        key: "failureReason",
        header: intl.formatMessage({ id: "analyzer.importIssues.reason" }),
      },
      {
        key: "receivedAt",
        header: intl.formatMessage({ id: "analyzer.importIssues.received" }),
      },
      {
        key: "action",
        header: intl.formatMessage({ id: "analyzer.importIssues.action" }),
      },
    ],
    [intl],
  );

  const eventRows = issues.eventRows.map((event) => ({
    id: String(event.id),
    eventType: <Tag type="red">{formatEventType(intl, event.eventType)}</Tag>,
    sourceId: event.sourceId || "-",
    targetReference: event.targetReference || "-",
    failureReason: formatFailureReason(intl, event.failureReason),
    receivedAt: event.receivedAt
      ? intl.formatDate(new Date(event.receivedAt), {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "-",
    action: event.analyzerId ? (
      <Link
        href={`/AnalyzerResults?id=${encodeURIComponent(event.analyzerId)}`}
      >
        {intl.formatMessage({ id: "analyzer.importIssues.openResults" })}
      </Link>
    ) : (
      "-"
    ),
  }));

  return (
    <Grid fullWidth data-testid="analyzer-import-issues">
      <Column lg={16} md={8} sm={4}>
        <Section>
          <Heading>
            {intl.formatMessage({ id: "analyzer.importIssues.title" })}
          </Heading>
          <p>
            {intl.formatMessage({ id: "analyzer.importIssues.description" })}
          </p>
        </Section>
        {loading ? (
          <InlineLoading
            description={intl.formatMessage({
              id: "analyzer.importIssues.loading",
            })}
          />
        ) : loadFailed ? (
          <InlineNotification
            kind="error"
            title={intl.formatMessage({
              id: "analyzer.importIssues.loadFailed",
            })}
            hideCloseButton
          />
        ) : eventRows.length === 0 ? (
          <InlineNotification
            kind="success"
            title={intl.formatMessage({ id: "analyzer.importIssues.empty" })}
            hideCloseButton
          />
        ) : (
          <>
            {eventRows.length > 0 ? (
              <ImportIssuesTable
                headers={eventHeaders}
                rows={eventRows}
                title={intl.formatMessage({
                  id: "analyzer.importIssues.events.title",
                })}
                description={intl.formatMessage({
                  id: "analyzer.importIssues.events.description",
                })}
              />
            ) : null}
          </>
        )}
        <DeliveryIssuesPanel />
      </Column>
    </Grid>
  );
};

export default ImportIssuesPanel;
